"use client";

import { useMemo, useSyncExternalStore } from "react";
import { useAuth } from "@/components/auth/AuthProvider";
import { permissionsForPlan, PLAN_FEATURES, type PaidPlanTier } from "@/lib/authz/planFeatures";
import type { FeatureKey } from "@/lib/authz/policy";
import { clearFreePreview, useIsPreviewingFreeUser } from "@/lib/home/freePreview";

/**
 * "Preview as plan" — a display-only admin affordance, sibling of
 * freePreview.ts and under the same safety model: a sessionStorage value
 * (tab-scoped, never sent to a server) that changes which tools the shell
 * shows as locked/unlocked and which home/nav branch renders. It never
 * reaches server authz (src/lib/server/authz.ts, app/(tools)/_guard.ts), and
 * `usePreviewPlan` returns null unless the caller passes a real,
 * server-verified `isAdmin`, so a stored value alone decides nothing.
 */
const STORAGE_KEY = "plan-preview-tier-v1";
const CHANGE_EVENT = "plan-preview-change";

function isPaidTier(value: string | null): value is PaidPlanTier {
  return value !== null && Object.prototype.hasOwnProperty.call(PLAN_FEATURES, value);
}

function readStoredTier(): PaidPlanTier | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return isPaidTier(raw) ? raw : null;
  } catch {
    // Blocked storage — treat as "not previewing".
    return null;
  }
}

function writeStoredTier(tier: PaidPlanTier | null) {
  if (typeof window === "undefined") return;
  try {
    if (tier) {
      window.sessionStorage.setItem(STORAGE_KEY, tier);
    } else {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Non-fatal — worst case the preview doesn't persist across navigation.
  }
  // Same-tab sessionStorage writes fire no `storage` event; broadcast our own.
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

function subscribe(callback: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, callback);
  window.addEventListener("storage", callback);
  return () => {
    window.removeEventListener(CHANGE_EVENT, callback);
    window.removeEventListener("storage", callback);
  };
}

function getServerSnapshot(): PaidPlanTier | null {
  return null;
}

/** The previewed paid plan, or null. `isAdmin` must be the real value from useAuth(). */
export function usePreviewPlan(isAdmin: boolean): PaidPlanTier | null {
  const stored = useSyncExternalStore(subscribe, readStoredTier, getServerSnapshot);
  return isAdmin ? stored : null;
}

/** Starts previewing `tier`; ends any free preview (the two are exclusive). */
export function enterPlanPreview(tier: PaidPlanTier): void {
  clearFreePreview();
  writeStoredTier(tier);
}

export function clearPlanPreview(): void {
  writeStoredTier(null);
}

/**
 * Preview-aware admin flag for customer-facing presentation. While an admin
 * previews a plan (or "free"), `displayIsAdmin` is false so admin-only buttons,
 * badges and overrides disappear exactly as they would for that customer.
 * `realIsAdmin` stays true for the preview switcher/exit control, /admin links
 * and anything that calls an admin-only server action. Display-only: never
 * feed this to authz. A non-admin is never previewing, whatever is stored.
 */
export interface PreviewAwareAdmin {
  realIsAdmin: boolean;
  isPreviewing: boolean;
  previewTier: PaidPlanTier | "free" | null;
  displayIsAdmin: boolean;
}

export function usePreviewAwareAdmin(): PreviewAwareAdmin {
  const { isAdmin } = useAuth();
  const plan = usePreviewPlan(isAdmin);
  const free = useIsPreviewingFreeUser(isAdmin);
  const previewTier: PaidPlanTier | "free" | null = plan ?? (free ? "free" : null);
  const isPreviewing = previewTier !== null;
  return useMemo(
    () => ({
      realIsAdmin: isAdmin,
      isPreviewing,
      previewTier,
      displayIsAdmin: isAdmin && !isPreviewing,
    }),
    [isAdmin, isPreviewing, previewTier],
  );
}

/**
 * Permissions + admin flag to use for tool lock/unlock DISPLAY. While an admin
 * previews a plan (or free) this returns that plan's permissions with
 * `isAdmin: false` (so `hasFeature` no longer short-circuits); otherwise the
 * real auth values. Keep the real `isAdmin` from useAuth() for admin links.
 */
export function useToolAccess(): {
  permissions: Record<FeatureKey, boolean>;
  isAdmin: boolean;
} {
  const { permissions, isAdmin } = useAuth();
  const { previewTier } = usePreviewAwareAdmin();
  return useMemo(
    () =>
      previewTier
        ? { permissions: permissionsForPlan(previewTier), isAdmin: false }
        : { permissions, isAdmin },
    [previewTier, permissions, isAdmin],
  );
}
